'use client';

import { ChevronUpIcon, EllipsisHorizontalIcon, SparklesIcon, SquaresPlusIcon, XMarkIcon } from '@heroicons/react/16/solid';
import type React from 'react';
import { useState } from 'react';
import { LoadingSpinner } from '@/frontend/design-system/atoms/LoadingSpinner';
import { Caption } from '@/frontend/design-system/typography/Caption';
import { Label2, Label3 } from '@/frontend/design-system/typography/Label';
import { P3 } from '@/frontend/design-system/typography/Paragraph';
import { cn } from '@/frontend/utils';
import type { ToolbarButton } from '../../_utils/buttons';
import { useDropdown } from './useDropdown';
type MoreDropdownProps = {
  moreButtons?: {
    textButtons?: ToolbarButton[];
    elementButtons?: ToolbarButton[];
  };
  onFormat?: () => Promise<void> | void;
  isEditable?: boolean;
};
export const MoreDropdown: React.FC<MoreDropdownProps> = ({
  moreButtons = {
    textButtons: [],
    elementButtons: []
  },
  onFormat,
  isEditable = true
}) => {
  const {
    textButtons = [],
    elementButtons = []
  } = moreButtons;
  const photoButton = textButtons.find(btn => btn.label === 'Zdjęcie');
  const filteredExtended = textButtons.filter(btn_0 => btn_0.label !== 'Zdjęcie');
  const filteredElementButtons = elementButtons.filter(btn_1 => btn_1.label !== 'Formatuj');
  const allElementsButtons = [...(photoButton ? [photoButton] : []), ...filteredElementButtons];
  const {
    open,
    setOpen,
    ref
  } = useDropdown();
  const [showMoreTools, setShowMoreTools] = useState<boolean>(false);
  const [formatting, setFormatting] = useState<boolean>(false);
  const handleFormatClick = async () => {
    if (!onFormat) return;
    setFormatting(true);
    try {
      await onFormat();
    } finally {
      setFormatting(false);
      setOpen(false);
    }
  };
  const renderButtons = (buttons: ToolbarButton[]) => buttons.map(({
    icon: Icon,
    label,
    action
  }) => <button type='button' key={label} onClick={action} disabled={!isEditable} className={cn('flex w-full cursor-default items-center gap-x-3 text-nowrap rounded-sm p-2 text-left hover:bg-backgroundHoverOverlay', !isEditable && 'pointer-events-none opacity-60')}>
        <Icon className='h-5 w-5 text-contentSecondary' />
        <P3>{label}</P3>
      </button>);
  if (textButtons.length === 0 && elementButtons.length === 0) return null;
  return <div className='relative' ref={ref}>
      <button type='button' onClick={() => {
      setOpen(!open);
      setShowMoreTools(false);
    }} className='flex cursor-default items-center gap-1 rounded-md p-1.5 transition-colors hover:bg-backgroundHoverOverlay'>
        {textButtons.length > 0 ? open ? <XMarkIcon className='h-4 w-4' /> : <EllipsisHorizontalIcon className='h-4 w-4' /> : <>
            <Label3 className='text-nowrap'>Więcej</Label3>
            <ChevronUpIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
          </>}
      </button>

      {open && !showMoreTools && <div className='absolute right-0 bottom-full mb-4 flex translate-x-2 flex-col items-end gap-2'>
          <button type='button' onClick={handleFormatClick} disabled={!isEditable || formatting} className={cn('flex w-fit cursor-default items-center gap-2 rounded-md border border-borderPrimary bg-backgroundTertiary px-2 py-1.5 shadow-sm hover:bg-backgroundPrimary', !isEditable && 'pointer-events-none bg-backgroundPrimary brightness-80')}>
            {formatting ? <LoadingSpinner size='button' className='border-contentPrimary border-t-transparent' /> : <SparklesIcon className='h-4 w-4' />}
            <Label2>Formatuj</Label2>
          </button>
          <button type='button' onClick={() => setShowMoreTools(true)} className='flex w-fit cursor-default items-center gap-2 rounded-md border border-borderPrimary bg-backgroundTertiary px-2 py-1.5 shadow-sm hover:bg-backgroundPrimary'>
            <SquaresPlusIcon className='h-4 w-4' />
            <Label2 className='text-nowrap'>Więcej narzędzi</Label2>
          </button>
        </div>}

      {open && showMoreTools && <div className='absolute right-0 bottom-full mb-3 w-60 translate-x-2 rounded-md border border-borderPrimary bg-backgroundTertiary p-3'>
          <button type='button' onClick={() => setShowMoreTools(false)} className='absolute right-3 cursor-default'>
            <XMarkIcon className='h-4 w-4' />
          </button>
          <div className='space-y-1'>
            {filteredExtended.length > 0 && <>
                <Caption className='text-contentTertiary'>Tekst</Caption>
                {renderButtons(filteredExtended)}
              </>}

            {allElementsButtons.length > 0 && <>
                <Caption className='text-contentTertiary'>Dodaj element</Caption>
                {renderButtons(allElementsButtons)}
              </>}
          </div>
        </div>}
    </div>;
};